import { ref, computed, onMounted } from 'vue';
import { useMasterDataStore } from '@/stores/masterData';

/**
 * Composable for accessing cached master data
 * 
 * This composable provides a simple interface for components to access
 * cached master data like users, employees, clients, departments, and products.
 * It automatically handles loading, caching, and provides reactive data.
 */
export function useMasterData() {
  const masterDataStore = useMasterDataStore();

  // Loading states
  const loading = ref({
    users: false,
    assignableUsers: false,
    employees: false,
    clients: false,
    departments: false,
    products: false,
    orderStatuses: false,
  });

  // Error states
  const errors = ref({
    users: null,
    assignableUsers: null,
    employees: null,
    clients: null,
    departments: null,
    products: null,
    orderStatuses: null,
  });

  // ===== USERS =====

  /**
   * Load users data
   */
  async function loadUsers(options = {}) {
    // We don't block on loading.value.users here because different parts of the app
    // might request users with different options (e.g., active vs all).
    // The store handles request deduplication via pendingRequests.

    loading.value.users = true;
    errors.value.users = null;

    try {
      await masterDataStore.getCachedData('users', options);
      return masterDataStore.users;
    } catch (error) {
      errors.value.users = error.message || 'Failed to load users';
      throw error;
    } finally {
      loading.value.users = false;
    }
  }

  /**
   * Get users (reactive computed)
   */
  const users = computed(() => masterDataStore.users);
  const activeUsers = computed(() => masterDataStore.activeUsers);

  /**
   * Get user by ID with automatic loading
   */
  async function getUser(id, autoLoad = true) {
    const user = masterDataStore.getUserById(id);

    if (!user && autoLoad && users.value.length === 0) {
      await loadUsers();
      return masterDataStore.getUserById(id);
    }

    return user;
  }

  // ===== ASSIGNABLE USERS (for order creation) =====

  /**
   * Load assignable users data (for order creation - requires only orders.create permission)
   */
  async function loadAssignableUsers(options = {}) {
    if (loading.value.assignableUsers) {
      console.log('⏳ Assignable users already loading, returning existing promise');
      return [];
    }

    loading.value.assignableUsers = true;
    errors.value.assignableUsers = null;

    console.log('👥 Loading assignable users with options:', options);

    try {
      const result = await masterDataStore.getCachedData('assignableUsers', options);
      console.log('✅ Assignable users loaded successfully:', {
        count: result?.length || 0,
        hasData: !!result && result.length > 0
      });
      return result;
    } catch (error) {
      console.error('❌ Failed to load assignable users:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      errors.value.assignableUsers = error.message || 'Failed to load assignable users';
      throw error;
    } finally {
      loading.value.assignableUsers = false;
    }
  }

  /**
   * Get assignable users (reactive computed)
   */
  const assignableUsers = computed(() => {
    // Since assignable users are stored separately, we need to get them from the cache
    const cacheKey = masterDataStore.generateCacheKey('assignableUsers', {});
    const cached = masterDataStore.getCachedResult(cacheKey);
    return cached ? cached.data : [];
  });

  // ===== EMPLOYEES =====

  /**
   * Load employees data
   */
  async function loadEmployees(options = {}) {
    // No blocking check here - let store handle deduplication

    // Check permission before attempting to load
    try {
      const { useAuthStore } = await import('../stores/auth');
      const authStore = useAuthStore();
      if (!authStore.hasPermission('employees.view')) {
        console.log('[useMasterData] User does not have employees.view permission, skipping load');
        return masterDataStore.employees; // Return empty array (default)
      }
    } catch (error) {
      // If we can't check permissions, proceed anyway (will fail gracefully)
      console.warn('[useMasterData] Could not check permissions, proceeding with load');
    }

    loading.value.employees = true;
    errors.value.employees = null;

    try {
      await masterDataStore.getCachedData('employees', options);
      return masterDataStore.employees;
    } catch (error) {
      // Don't set error for permission denied - it's expected
      if (error.response?.status === 403 || error.message === 'Permission denied') {
        console.log('[useMasterData] Permission denied for employees, returning empty array');
        return masterDataStore.employees; // Return empty array
      }
      errors.value.employees = error.message || 'Failed to load employees';
      throw error;
    } finally {
      loading.value.employees = false;
    }
  }

  /**
   * Get employees (reactive computed)
   */
  const employees = computed(() => masterDataStore.employees);
  const activeEmployees = computed(() => masterDataStore.activeEmployees);

  /**
   * Get employee by ID with automatic loading
   */
  async function getEmployee(id, autoLoad = true) {
    const employee = masterDataStore.getEmployeeById(id);

    if (!employee && autoLoad && employees.value.length === 0) {
      await loadEmployees();
      return masterDataStore.getEmployeeById(id);
    }

    return employee;
  }

  /**
   * Get employee by user ID with automatic loading
   */
  async function getEmployeeByUserId(userId, autoLoad = true) {
    const employee = masterDataStore.getEmployeeByUserId(userId);

    if (!employee && autoLoad && employees.value.length === 0) {
      try {
        await loadEmployees();
        return masterDataStore.getEmployeeByUserId(userId);
      } catch (error) {
        // If it's a permission error (403), return null gracefully
        if (error.response?.status === 403 || error.message?.includes('403') || error.message?.includes('Permission denied')) {
          console.log('[useMasterData] User does not have permission to view employees, returning null');
          return null;
        }
        // Re-throw other errors
        throw error;
      }
    }

    return employee;
  }

  // ===== CLIENTS =====

  /**
   * Load clients data
   */
  async function loadClients(options = {}) {
    // No blocking check here - let store handle deduplication

    loading.value.clients = true;
    errors.value.clients = null;

    try {
      // If this is a filtered/paginated request, return the specific results
      const hasFilters = options.page || options.limit || options.search ||
        options.classification || options.is_active ||
        options.sort_by || options.sort_order || options.payment_options;

      if (hasFilters) {
        // Return specific filtered results (may include pagination metadata)
        const result = await masterDataStore.getCachedData('clients', options);
        return result;
      } else {
        // For basic requests, load into store and return computed property
        const result = await masterDataStore.getCachedData('clients', options);

        // If result has pagination structure, extract the data array for the store
        if (result && typeof result === 'object' && result.data && Array.isArray(result.data)) {
          // This shouldn't happen for basic requests, but handle gracefully
          return result.data;
        }

        return masterDataStore.clients;
      }
    } catch (error) {
      errors.value.clients = error.message || 'Failed to load clients';
      throw error;
    } finally {
      loading.value.clients = false;
    }
  }

  /**
   * Get clients (reactive computed)
   */
  const clients = computed(() => masterDataStore.clients);
  const activeClients = computed(() => masterDataStore.activeClients);

  /**
   * Get client by ID with automatic loading
   */
  async function getClient(id, autoLoad = true) {
    const client = masterDataStore.getClientById(id);

    if (!client && autoLoad && clients.value.length === 0) {
      await loadClients();
      return masterDataStore.getClientById(id);
    }

    return client;
  }

  // ===== DEPARTMENTS =====

  /**
   * Load departments data
   */
  async function loadDepartments(options = {}) {
    // No blocking check here - let store handle deduplication

    loading.value.departments = true;
    errors.value.departments = null;

    try {
      await masterDataStore.getCachedData('departments', options);
      return masterDataStore.departments;
    } catch (error) {
      errors.value.departments = error.message || 'Failed to load departments';
      throw error;
    } finally {
      loading.value.departments = false;
    }
  }

  /**
   * Get departments (reactive computed)
   */
  const departments = computed(() => masterDataStore.departments);
  const activeDepartments = computed(() => masterDataStore.activeDepartments);

  /**
   * Get department by ID with automatic loading
   */
  async function getDepartment(id, autoLoad = true) {
    const department = masterDataStore.getDepartmentById(id);

    if (!department && autoLoad && departments.value.length === 0) {
      await loadDepartments();
      return masterDataStore.getDepartmentById(id);
    }

    return department;
  }

  // ===== PRODUCTS =====

  /**
   * Load products data
   */
  async function loadProducts(options = {}) {
    // No blocking check here - let store handle deduplication

    loading.value.products = true;
    errors.value.products = null;

    try {
      // Check if this is a filtered request (beyond basic active filtering)
      const hasSignificantFilters = options.search || options.category_id || options.limit || options.page;

      if (hasSignificantFilters) {
        // Return specific filtered results
        const result = await masterDataStore.getCachedData('products', options);
        return result;
      } else {
        // For basic requests (including basic is_active filter), load into store and return computed property
        await masterDataStore.getCachedData('products', options);
        return masterDataStore.products;
      }
    } catch (error) {
      errors.value.products = error.message || 'Failed to load products';
      throw error;
    } finally {
      loading.value.products = false;
    }
  }

  /**
   * Get products (reactive computed)
   */
  const products = computed(() => masterDataStore.products);
  const activeProducts = computed(() => masterDataStore.activeProducts);

  /**
   * Get product by ID with automatic loading
   */
  async function getProduct(id, autoLoad = true) {
    const product = masterDataStore.getProductById(id);

    if (!product && autoLoad && products.value.length === 0) {
      await loadProducts();
      return masterDataStore.getProductById(id);
    }

    return product;
  }

  // ===== ORDER STATUSES =====

  /**
   * Load order statuses data
   */
  async function loadOrderStatuses(options = {}) {
    // No blocking check here - let store handle deduplication

    loading.value.orderStatuses = true;
    errors.value.orderStatuses = null;

    try {
      // Order statuses are typically a simple list without complex filtering
      await masterDataStore.getCachedData('orderStatuses', options);
      return masterDataStore.orderStatuses;
    } catch (error) {
      errors.value.orderStatuses = error.message || 'Failed to load order statuses';
      throw error;
    } finally {
      loading.value.orderStatuses = false;
    }
  }

  /**
   * Get order statuses (reactive computed)
   */
  const orderStatuses = computed(() => masterDataStore.orderStatuses);

  // ===== UTILITY FUNCTIONS =====

  /**
   * Load all essential data at once
   */
  async function loadEssentialData() {
    const promises = [
      loadUsers(),
      loadEmployees(),
      loadDepartments(),
    ];

    try {
      await Promise.all(promises);
    } catch (error) {
      console.error('[useMasterData] Failed to load essential data:', error);
      throw error;
    }
  }

  /**
   * Load all data at once
   */
  async function loadAllData() {
    const promises = [
      loadUsers(),
      loadEmployees(),
      loadClients(),
      loadDepartments(),
      loadProducts(),
      loadOrderStatuses(),
    ];

    try {
      await Promise.all(promises);
    } catch (error) {
      console.error('[useMasterData] Failed to load all data:', error);
      throw error;
    }
  }

  /**
   * Refresh specific data type
   */
  async function refresh(dataType) {
    masterDataStore.invalidate(dataType);

    switch (dataType) {
      case 'users':
        return await loadUsers();
      case 'employees':
        return await loadEmployees();
      case 'clients':
        return await loadClients();
      case 'departments':
        return await loadDepartments();
      case 'products':
        return await loadProducts();
      case 'orderStatuses':
        return await loadOrderStatuses();
      default:
        throw new Error(`Unknown data type: ${dataType}`);
    }
  }

  /**
   * Refresh all data
   */
  async function refreshAll() {
    masterDataStore.clearCache();
    return await loadAllData();
  }

  /**
   * Invalidate cache when data changes
   */
  function invalidateOnChange(dataType, operation = null) {
    masterDataStore.invalidateOnChange(dataType, operation);
  }

  /**
   * Get cache statistics
   */
  function getStats() {
    return masterDataStore.getStats();
  }

  // ===== COMPUTED STATE =====

  const isLoading = computed(() =>
    Object.values(loading.value).some(state => state)
  );

  const hasErrors = computed(() =>
    Object.values(errors.value).some(error => error !== null)
  );

  const allErrors = computed(() =>
    Object.entries(errors.value)
      .filter(([key, error]) => error !== null)
      .reduce((acc, [key, error]) => {
        acc[key] = error;
        return acc;
      }, {})
  );

  // Exported interface
  return {
    // Loading states
    loading,
    errors,

    // Data loaders
    loadUsers,
    loadAssignableUsers,
    loadEmployees,
    loadClients,
    loadDepartments,
    loadProducts,
    loadOrderStatuses,

    // Bulk data operations
    loadEssentialData,
    loadAllData,

    // Reactive data
    users,
    activeUsers,
    assignableUsers,
    employees,
    activeEmployees,
    clients,
    activeClients,
    departments,
    activeDepartments,
    products,
    activeProducts,
    orderStatuses,

    // Data getters with auto-loading
    getUser,
    getEmployee,
    getEmployeeByUserId,
    getClient,
    getDepartment,
    getProduct,

    // Utilities
    refresh,
    refreshAll,
    invalidateOnChange,
    getStats
  };
}

/**
 * Specialized composable for user/employee data
 * This is the most commonly used data and provides additional helper functions
 */
export function useUserEmployeeData() {
  const masterData = useMasterData();

  /**
   * Load user and employee data together (most common use case)
   */
  async function loadUserEmployeeData() {
    try {
      await Promise.all([
        masterData.loadUsers(),
        masterData.loadEmployees(),
      ]);
    } catch (error) {
      console.error('[useUserEmployeeData] Failed to load user/employee data:', error);
      throw error;
    }
  }

  /**
   * Find user name by ID (common for display purposes)
   */
  async function getUserName(userId) {
    const user = await masterData.getUser(userId);
    if (!user) return 'Unknown User';

    return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User';
  }

  /**
   * Find employee name by ID (common for display purposes)
   */
  async function getEmployeeName(employeeId) {
    const employee = await masterData.getEmployee(employeeId);
    if (!employee) return 'Unknown Employee';

    return `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown Employee';
  }

  /**
   * Get employee data for current user
   */
  async function getCurrentUserEmployee(userId) {
    if (!userId) return null;

    try {
      return await masterData.getEmployeeByUserId(userId);
    } catch (error) {
      // If it's a permission error (403), return null gracefully
      if (error.response?.status === 403 || error.message?.includes('403') || error.message?.includes('Permission denied')) {
        console.log('[useUserEmployeeData] User does not have permission to view employees, returning null');
        return null;
      }
      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Get all users formatted for dropdowns
   */
  const usersForDropdown = computed(() =>
    masterData.activeUsers.value.map(user => ({
      value: user.id,
      label: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      email: user.email,
      role: user.role,
    }))
  );

  /**
   * Get all employees formatted for dropdowns
   */
  const employeesForDropdown = computed(() =>
    masterData.activeEmployees.value.map(employee => ({
      value: employee.id,
      label: `${employee.first_name || ''} ${employee.last_name || ''}`.trim(),
      department: employee.department_name,
      position: employee.position,
    }))
  );

  return {
    // Base functionality
    ...masterData,

    // Specialized loaders
    loadUserEmployeeData,

    // Helper functions
    getUserName,
    getEmployeeName,
    getCurrentUserEmployee,

    // Formatted data for UI
    usersForDropdown,
    employeesForDropdown,
  };
}

export default useMasterData; 